// THE SINGLE SOURCE OF TRUTH FOR PAGES: id → component, title, nav group, flags.
//
// The sidebar, the route parser and the guards all derive from this. Never duplicate a page
// list anywhere else — a page that exists is a row here, and nothing else.
import type { ComponentType } from 'react';
import './registry.de.js';
import './registry.fr.js';

import TodayPage from './TodayPage.js';
import ReviewPage from './ReviewPage.js';
import InboxPage from './InboxPage.js';
import PeoplePage from './PeoplePage.js';
import PersonPage from './PersonPage.js';
import OrganisationsPage from './OrganisationsPage.js';
import OrganisationPage from './OrganisationPage.js';
import PipelinePage from './PipelinePage.js';
import SearchPage from './SearchPage.js';
import MarketPage from './MarketPage.js';
import NetworkPage from './NetworkPage.js';
import SignalsPage from './SignalsPage.js';
import CampaignsPage from './CampaignsPage.js';
import AgentsPage from './AgentsPage.js';
import RunPage from './RunPage.js';
import SourcesPage from './SourcesPage.js';
import AdminPage from './AdminPage.js';

export type PageId =
  | 'today' | 'review' | 'inbox'
  | 'people' | 'person' | 'orgs' | 'org' | 'pipeline' | 'search' | 'market' | 'network'
  | 'signals' | 'campaigns'
  | 'agents' | 'run' | 'sources' | 'admin';

export type NavGroup = 'Overview' | 'Network' | 'Outreach' | 'System';

/**
 * What every page component is handed. List pages ignore all three; the three detail pages read
 * the one that addresses them and must render honestly when it is undefined (the parser already
 * redirects a garbled `#person?id=` to the list, so in practice it is set).
 */
export interface PageProps {
  personId?: number;
  organisationId?: number;
  /** The Run desk's subject (`#run?id=N`, BUILD-BRIEF-R3f §2). */
  runId?: number;
}

export interface PageDef {
  id: PageId;
  title: string;
  navGroup: NavGroup;
  component: ComponentType<PageProps>;
  /** Not a sidebar entry. The two detail routes are reached from a row, not from the nav. */
  hidden?: boolean;
  /**
   * Which nav item stays lit while this page is open. A detail page names its list, so
   * `#person?id=7` keeps People lit and `#org?id=3` keeps Organisations lit.
   */
  navOwner?: PageId;
  adminOnly?: boolean;
  /**
   * The endpoint whose count this nav item shows, when it has one. AppShell polls the two
   * summaries every 15 s and tolerates a 404 — a route nobody has built yet must leave the nav
   * silent, never show a zero it did not count.
   */
  activity?: 'proposals' | 'today.signals' | 'inbox.needs_me';
}

export const PAGES: PageDef[] = [
  { id: 'today',     title: 'Today',         navGroup: 'Overview', component: TodayPage },
  { id: 'review',    title: 'Review',        navGroup: 'Overview', component: ReviewPage, activity: 'proposals' },
  { id: 'inbox',     title: 'Inbox',         navGroup: 'Overview', component: InboxPage, activity: 'inbox.needs_me' },

  { id: 'people',    title: 'People',        navGroup: 'Network',  component: PeoplePage },
  { id: 'person',    title: 'Person',        navGroup: 'Network',  component: PersonPage,       hidden: true, navOwner: 'people' },
  { id: 'orgs',      title: 'Organisations', navGroup: 'Network',  component: OrganisationsPage },
  { id: 'org',       title: 'Organisation',  navGroup: 'Network',  component: OrganisationPage, hidden: true, navOwner: 'orgs' },
  { id: 'pipeline',  title: 'Pipeline',      navGroup: 'Network',  component: PipelinePage },
  { id: 'search',    title: 'Search',        navGroup: 'Network',  component: SearchPage },
  { id: 'market',    title: 'Market',        navGroup: 'Network',  component: MarketPage },
  { id: 'network',   title: 'Network',       navGroup: 'Network',  component: NetworkPage },

  { id: 'signals',   title: 'Signals',       navGroup: 'Outreach', component: SignalsPage, activity: 'today.signals' },
  { id: 'campaigns', title: 'Campaigns',     navGroup: 'Outreach', component: CampaignsPage },

  { id: 'agents',    title: 'Agents',        navGroup: 'System',   component: AgentsPage },
  { id: 'run',       title: 'Run',           navGroup: 'System',   component: RunPage,          hidden: true, navOwner: 'agents' },
  { id: 'sources',   title: 'Sources',       navGroup: 'System',   component: SourcesPage },
  { id: 'admin',     title: 'Admin',         navGroup: 'System',   component: AdminPage, adminOnly: true },
];

export const DEFAULT_PAGE: PageId = 'today';

export const NAV_GROUPS: NavGroup[] = ['Overview', 'Network', 'Outreach', 'System'];

/** Retired ids kept alive as aliases, so a link already in a notification keeps working. */
const ALIASES: Record<string, PageId> = {
  organisations: 'orgs',
  proposals: 'review',
};

export function findPage(id: string): PageDef | undefined {
  const canonical = ALIASES[id] ?? id;
  return PAGES.find((p) => p.id === canonical);
}

/** The nav item to light while `page` is open — itself, or the list a detail page belongs to. */
export function navOwnerOf(page: PageId): PageId {
  return findPage(page)?.navOwner ?? page;
}
